
import "@/sass/globals.scss";

import Vue from "vue";
import {
  Condition,
  RuleGroup,
  Trigger,
  VuetifyShapeForSelect
} from "@/types/conditions";
import {
  TemplateSettings,
  TemplatesManagementData,
  TemplateWithValidSettings,
  TemplateParents,
  SupportedPostType,
  Template
} from "./types";
import { Component, Prop } from "vue-property-decorator";
import defensiveCopy from "@/util/defensive-copy";
import ConditionsMatrix from "@/components/Reusable/CustomInputs/Conditions/ConditionsMatrix.vue";
import {
  getConditionsThatApplyToTemplateTypes,
  getConditionsThatAreAvailableForTemplateType,
  removeEmptyRulesFromRuleGroup
} from "@/lib/conditions/conditions";
import DropdownInput from "@/components/Reusable/CustomInputs/DropdownInput.vue";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import NumberInput from "@/components/Reusable/CustomInputs/NumberInput.vue";
import lodash from "lodash";
import {
  removeChangesNotSavedWarning,
  warnIfExitingWithUnsavedChanges
} from "@/util/warn-unsaved-changes";
import { saveTemplateToWordPress } from "./lib/api";
import { pipe } from "fp-ts/lib/function";
import { fold } from "fp-ts/lib/Either";
import NotificationLogger from "@/lib/notifications/notifications";
import { centerDialogInScreen } from "@/subapps/manage-templates/lib/modal-position";
import { checkIfTemplateSettingsAlreadyExistsAndConfirmToContinue } from "@/subapps/manage-templates/lib/utils";
import isEqual from "lodash/isEqual";
import Icon from "@/components/UI/Icon.vue";
import Dialog from "@/components/UI/Dialog.vue";
import TriggersMatrix from "@/components/Reusable/CustomInputs/Triggers/TriggersMatrix.vue";
import ToggleInput from "@/components/Reusable/CustomInputs/ToggleInput.vue";
import FreeModeAlertForConditionsUsed from "@/components/Subscription/FreeModeAlertForConditionsUsed.vue";
import FreeModeAlertForPopupTriggers from "@/components/Subscription/FreeModeAlertForPopupTriggers.vue";
import {
  getBreakdanceFooterPostType,
  getBreakdanceHeaderPostType,
  getBreakdanceTemplatePostType
} from "@/lib/constants/post-types";

@Component({
  components: {
    FreeModeAlertForConditionsUsed,
    FreeModeAlertForPopupTriggers,
    ToggleInput,
    TriggersMatrix,
    Icon,
    NumberInput,
    TextInput,
    DropdownInput,
    ConditionsMatrix,
    Dialog
  }
})
export default class EditTemplateModal extends Vue {
  @Prop()
  value!: TemplateWithValidSettings;

  @Prop()
  postType!: SupportedPostType;

  @Prop()
  mode!: "add" | "edit";

  @Prop()
  templatesManagementData!: TemplatesManagementData;

  @Prop()
  templateParents!: TemplateParents;

  @Prop()
  templates!: Template[];

  @Prop({ default: false })
  skipWarning!: boolean;

  @Prop({ default: false })
  canDisable!: boolean;

  @Prop({ default: false })
  hasPermissionToManageFallbacks!: boolean;

  internalValue: TemplateWithValidSettings = defensiveCopy(this.value);

  warnIfUnsavedFunction!: (e: Event) => void;

  editingAdvancedSettings = false;

  acknowledgeWarning() {
    this.$emit("skipWarning", true);
  }

  beforeMount() {
    if (this.value.postType !== getBreakdanceTemplatePostType()) {
      this.acknowledgeWarning();
    }
  }

  mounted() {
    centerDialogInScreen();

    this.warnIfUnsavedFunction = warnIfExitingWithUnsavedChanges(
      () => this.hasUnsavedChanges
    );
  }

  get templateName() {
    if (this.postType === getBreakdanceTemplatePostType()) return "template";
    if (this.postType === getBreakdanceFooterPostType()) return "footer";
    if (this.postType === getBreakdanceHeaderPostType()) return "header";
    if (this.postType === "breakdance_popup") return "popup";

    return "Unsupported template";
  }

  get templateTypesDropdownInVuetifyShape(): VuetifyShapeForSelect[] {
    const vuetifyShape: VuetifyShapeForSelect[] = [];

    this.templatesManagementData.templateTypeCategories.forEach(
      templateTypeCategory => {
        vuetifyShape.push({ header: templateTypeCategory.label });

        vuetifyShape.push(
          ...templateTypeCategory.types.map(templateType => {
            return {
              category: templateTypeCategory.label,
              text: templateType.label,
              value: templateType.slug
            };
          })
        );

        vuetifyShape.push({ divider: true });
      }
    );

    vuetifyShape.pop();

    return vuetifyShape;
  }

  get selectedLocationCategory() {
    return this.templatesManagementData.templateTypeCategories.find(
      templateTypeCategory =>
        templateTypeCategory.types.find(
          type => type.slug === this.internalValue.settings.type
        )
    );
  }

  get categoryLabelForSelectedLocation() {
    return this.selectedLocationCategory?.label;
  }

  get labelForSelectedLocation() {
    return (
      this.selectedLocationCategory?.types.find(
        type => type.slug === this.internalValue.settings.type
      )?.label || this.internalValue.settings.type
    );
  }

  get labelForInheritingParent() {
    return (
      this.templateParents.find(
        parent => parent.value === this.internalValue.settings.parentId
      )?.text || `Parent with ID: ${this.internalValue.settings.parentId}`
    );
  }

  get availableConditions(): Condition[] {
    const availableConditions = getConditionsThatAreAvailableForTemplateType(
      this.templatesManagementData.conditions,
      this.internalValue.settings.type
    );

    if (this.internalValue.settings.type) {
      return getConditionsThatApplyToTemplateTypes(availableConditions, [
        this.internalValue.settings.type
      ]).filter(condition => condition.supports.includes("templating"));
    }

    return availableConditions.filter(condition =>
      condition.supports.includes("templating")
    );
  }

  get templateParentsExcludingCurrentTemplate() {
    const templatesExcludingSelf = this.templateParents.filter(
      parent => this.value.id !== parent.value
    );

    if (
      this.inheritedTemplateNoLongerExists &&
      this.internalValue.settings.parentId
    ) {
      templatesExcludingSelf.unshift({
        text: `Selected template with ID: ${this.internalValue.settings.parentId} no longer exists.`,
        value: this.internalValue.settings.parentId
      });
    }

    return templatesExcludingSelf;
  }

  get inheritedTemplateNoLongerExists() {
    if (!this.internalValue.settings.parentId) return false;

    return !this.templateParents.find(
      template => template.value === this.internalValue.settings.parentId
    );
  }

  get inheritanceUiEnabled() {
    return this.postType === getBreakdanceTemplatePostType();
  }

  setTemplateTitle(newTitle: string | null) {
    this.internalValue = {
      ...this.internalValue,
      title: newTitle || ""
    };
  }

  updateTemplateSettings(newTemplateSettings: TemplateSettings) {
    this.internalValue.settings = defensiveCopy(newTemplateSettings);
  }

  saveInProgress = false;
  attemptedToSave = false;

  async saveTemplate() {
    this.attemptedToSave = true;

    if (this.isThereAnError) return;

    const cleanTemplate = defensiveCopy(this.internalValue);

    if (!cleanTemplate.settings.priority) {
      cleanTemplate.settings.priority = 1;
    }

    const settingsChanged = !isEqual(
      this.value.settings,
      cleanTemplate.settings
    );

    if (
      settingsChanged &&
      !checkIfTemplateSettingsAlreadyExistsAndConfirmToContinue(
        this.templates,
        cleanTemplate.settings,
        this.templateName
      )
    ) {
      return;
    }

    this.saveInProgress = true;

    cleanTemplate.settings.ruleGroups =
      (cleanTemplate.settings.ruleGroups &&
        removeEmptyRulesFromRuleGroup(cleanTemplate.settings.ruleGroups)) ||
      [];
    cleanTemplate.settings.triggers =
      (cleanTemplate.settings.triggers &&
        cleanTemplate.settings.triggers.filter((trigger: Trigger) => {
          return Object.keys(trigger).length > 0;
        })) ||
      [];
    cleanTemplate.settings.type = cleanTemplate.settings.type || "none";
    cleanTemplate.title = cleanTemplate.title || `New ${this.postType}`;

    pipe(
      await saveTemplateToWordPress(cleanTemplate),
      fold(
        () => {
          NotificationLogger.log.message(
            `${this.templateName} was not saved, or it was saved but the value is invalid`
          );
        },
        savedTemplate => {
          this.$emit("input", savedTemplate);
        }
      )
    );

    this.saveInProgress = false;

    this.closeDialog();
  }

  setTemplateType(templateType: string | undefined) {
    const newTemplateSettings = defensiveCopy(this.internalValue.settings);

    newTemplateSettings.type = templateType;

    if (
      !this.internalValue.settings ||
      newTemplateSettings.type !== this.internalValue.settings.type
    ) {
      newTemplateSettings.ruleGroups = [];
    }

    if (newTemplateSettings.type) {
      newTemplateSettings.priority = this.getDefaultTemplatePriorityBasedOnType(
        newTemplateSettings.type
      );
    }

    this.updateTemplateSettings(newTemplateSettings);
  }

  setTemplateParent(templateParent: number | "") {
    const newTemplateSettings = defensiveCopy(this.internalValue.settings);

    if (!templateParent) {
      delete newTemplateSettings.parentId;
    } else {
      newTemplateSettings.parentId = templateParent;
    }

    this.updateTemplateSettings(newTemplateSettings);
  }

  setTemplatePriority(templatePriority: number | null) {
    const newTemplateSettings = defensiveCopy(this.internalValue.settings);

    newTemplateSettings.priority = templatePriority ?? undefined;

    this.updateTemplateSettings(newTemplateSettings);
  }

  getDefaultTemplatePriorityBasedOnType(typeSlug: string) {
    return this.templatesManagementData.templateTypeCategories
      .flatMap(c => c.types)
      .find(t => t.slug === typeSlug)?.defaultPriority;
  }

  updateRuleGroups(newRuleGroups: RuleGroup[]) {
    const newTemplateSettings = defensiveCopy(this.internalValue.settings);
    newTemplateSettings.ruleGroups = newRuleGroups;

    this.updateTemplateSettings(newTemplateSettings);
  }

  updateTriggers(newTriggers: Trigger[]) {
    const newTemplateSettings = defensiveCopy(this.internalValue.settings);
    newTemplateSettings.triggers = newTriggers;

    this.updateTemplateSettings(newTemplateSettings);
  }

  get locationError() {
    if (!this.attemptedToSave) return false;

    if (!this.internalValue.settings.type) {
      return "Location is required";
    }

    return false;
  }

  get titleError() {
    if (!this.attemptedToSave) return false;

    if (!this.internalValue.title) {
      return "Title is required";
    }

    return false;
  }

  get priorityError() {
    if (!this.attemptedToSave) return false;

    if (this.internalValue.settings.priority === undefined) {
      return false;
    }

    if (this.postType === "breakdance_popup") {
      return false;
    }

    if (this.internalValue.settings.priority < 1) {
      return "The minimum priority is 1";
    }

    return false;
  }

  get isThereAnError() {
    return !!(this.priorityError || this.titleError || this.locationError);
  }

  get hasUnsavedChanges() {
    return !lodash.isEqual(this.internalValue, this.value);
  }

  closeDialog() {
    removeChangesNotSavedWarning(this.warnIfUnsavedFunction);

    this.$emit("close");
  }

  setFallback(fallback: boolean) {
    const newTemplateSettings = defensiveCopy(this.internalValue.settings);
    newTemplateSettings.fallback = fallback;
    this.updateTemplateSettings(newTemplateSettings);
  }

  updateDisabled(enabled: boolean) {
    const newTemplateSettings = defensiveCopy(this.internalValue.settings);
    newTemplateSettings.disabled = !enabled;
    this.updateTemplateSettings(newTemplateSettings);
  }

  setOnlyShowOnce(onlyShowOnce: boolean) {
    const newTemplateSettings = defensiveCopy(this.internalValue.settings);
    newTemplateSettings.onlyShowOnce = onlyShowOnce;
    this.updateTemplateSettings(newTemplateSettings);
  }

  setAvoidMultiple(avoidMultiple: boolean) {
    const newTemplateSettings = defensiveCopy(this.internalValue.settings);
    newTemplateSettings.avoidMultiple = avoidMultiple;
    this.updateTemplateSettings(newTemplateSettings);
  }

  onlyShowOpen = false;
  toggleOnlyShow(value: boolean) {
    if (!value) {
      this.setLimitPageLoad(null);
      this.setLimitSession(null);
      this.setLimitForever(null);
    }
    this.onlyShowOpen = value;
  }

  get hasOnlyShow() {
    if (this.onlyShowOpen === true) {
      return true;
    }
    return (
      this.internalValue.settings.limitForever ||
      this.internalValue.settings.limitSession ||
      this.internalValue.settings.limitPageLoad
    );
  }

  setLimitPageLoad(limit: number | null) {
    const newTemplateSettings = defensiveCopy(this.internalValue.settings);
    newTemplateSettings.limitPageLoad = limit;
    this.updateTemplateSettings(newTemplateSettings);
  }

  setLimitSession(limit: number | null) {
    const newTemplateSettings = defensiveCopy(this.internalValue.settings);
    newTemplateSettings.limitSession = limit;
    this.updateTemplateSettings(newTemplateSettings);
  }

  setLimitForever(limit: number | null) {
    const newTemplateSettings = defensiveCopy(this.internalValue.settings);
    newTemplateSettings.limitForever = limit;
    this.updateTemplateSettings(newTemplateSettings);
  }
}
